const PERSONALITIES = new Set(["professional", "calm", "focus"]);
const COLOR_MODES = new Set(["light", "dark", "system"]);
const DENSITIES = new Set(["comfortable", "standard", "compact"]);
const ACCENTS = new Set(["blue", "copper", "forest", "slate", "ocean", "system"]);
const MOTIONS = new Set(["full", "subtle", "reduced"]);
const READER_WIDTHS = new Set(["focused", "balanced", "wide"]);
const PLAN_RANK = Object.freeze({ preview: 0, pro: 1, teams: 2, business: 3, enterprise: 4 });

export const APPEARANCE_SCHEMA_VERSION = 2;
export const APPEARANCE_STORAGE_KEY = "kahvor:appearance:v2";

export const DEFAULT_APPEARANCE = Object.freeze({
  version: APPEARANCE_SCHEMA_VERSION,
  personality: "professional",
  colorMode: "system",
  density: "standard",
  accent: "blue",
  motion: "subtle",
  readerWidth: "balanced",
  display: Object.freeze({
    showMessagePreviewText: true,
    previewLineCount: 2,
    showAvatars: true,
    emphasizeUnreadMessages: true,
    showCompactTimestamps: false,
    messageBodyScale: 1,
    reduceTranslucency: false,
    highContrast: false,
    useSystemFont: true,
    displaySidebarLabelsWhenCollapsed: false,
    showAIRailAutomatically: true,
    rememberLastSidebarState: true
  }),
  schedule: Object.freeze({
    enabled: false,
    workStart: "09:00",
    workEnd: "17:00",
    workPersonality: "focus",
    afterHoursPersonality: "calm"
  }),
  deviceOverride: Object.freeze({ enabled: false, colorMode: null, density: null }),
  onboardingComplete: false
});

const cloneDefaults = () => ({
  ...DEFAULT_APPEARANCE,
  display: { ...DEFAULT_APPEARANCE.display },
  schedule: { ...DEFAULT_APPEARANCE.schedule },
  deviceOverride: { ...DEFAULT_APPEARANCE.deviceOverride }
});

function enumValue(value, allowed, fallback, aliases = {}) {
  const normalized = String(value ?? "").trim().toLowerCase().replaceAll("_", "-");
  const migrated = aliases[normalized] || normalized;
  return allowed.has(migrated) ? migrated : fallback;
}

function booleanValue(value, fallback) {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "").trim().toLowerCase();
  if (["true", "1", "yes", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "off"].includes(normalized)) return false;
  return fallback;
}

function boundedNumber(value, fallback, minimum, maximum, integer = false) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const bounded = Math.min(Math.max(parsed, minimum), maximum);
  return integer ? Math.trunc(bounded) : bounded;
}

function validClock(value, fallback) {
  const normalized = String(value ?? "");
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(normalized) ? normalized : fallback;
}

export function normalizeAppearance(value = {}, fallback = DEFAULT_APPEARANCE) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const base = fallback && typeof fallback === "object" ? fallback : DEFAULT_APPEARANCE;
  const sourceDisplay = source.display && typeof source.display === "object" ? source.display : source;
  const sourceSchedule = source.schedule && typeof source.schedule === "object"
    ? source.schedule
    : source.automaticScheduleConfiguration && typeof source.automaticScheduleConfiguration === "object"
      ? source.automaticScheduleConfiguration
      : {};
  const sourceDevice = source.deviceOverride && typeof source.deviceOverride === "object" ? source.deviceOverride : {};
  const result = cloneDefaults();

  result.personality = enumValue(
    source.personality ?? source.appearancePersonality,
    PERSONALITIES,
    base.personality || DEFAULT_APPEARANCE.personality
  );
  result.colorMode = enumValue(
    source.colorMode ?? source.mode ?? source.theme,
    COLOR_MODES,
    base.colorMode || DEFAULT_APPEARANCE.colorMode,
    { porcelain: "light", "porcelain-ember": "light" }
  );
  result.density = enumValue(
    source.density,
    DENSITIES,
    base.density || DEFAULT_APPEARANCE.density,
    { spacious: "comfortable" }
  );
  result.accent = enumValue(
    source.accent,
    ACCENTS,
    base.accent || DEFAULT_APPEARANCE.accent,
    { halo: "blue", ember: "copper", bloom: "slate", "kahvor-copper": "copper" }
  );
  result.motion = enumValue(source.motion, MOTIONS, base.motion || DEFAULT_APPEARANCE.motion);
  result.readerWidth = enumValue(
    source.readerWidth,
    READER_WIDTHS,
    base.readerWidth || DEFAULT_APPEARANCE.readerWidth
  );

  const baseDisplay = base.display || DEFAULT_APPEARANCE.display;
  result.display = {
    showMessagePreviewText: booleanValue(sourceDisplay.showMessagePreviewText, baseDisplay.showMessagePreviewText),
    previewLineCount: boundedNumber(sourceDisplay.previewLineCount, baseDisplay.previewLineCount, 1, 3, true),
    showAvatars: booleanValue(sourceDisplay.showAvatars, baseDisplay.showAvatars),
    emphasizeUnreadMessages: booleanValue(sourceDisplay.emphasizeUnreadMessages, baseDisplay.emphasizeUnreadMessages),
    showCompactTimestamps: booleanValue(sourceDisplay.showCompactTimestamps, baseDisplay.showCompactTimestamps),
    messageBodyScale: boundedNumber(sourceDisplay.messageBodyScale, baseDisplay.messageBodyScale, 0.85, 1.4),
    reduceTranslucency: booleanValue(sourceDisplay.reduceTranslucency ?? sourceDisplay.reduceTranslucencyOverride, baseDisplay.reduceTranslucency),
    highContrast: booleanValue(sourceDisplay.highContrast ?? sourceDisplay.highContrastOverride, baseDisplay.highContrast),
    useSystemFont: booleanValue(sourceDisplay.useSystemFont, baseDisplay.useSystemFont),
    displaySidebarLabelsWhenCollapsed: booleanValue(sourceDisplay.displaySidebarLabelsWhenCollapsed, baseDisplay.displaySidebarLabelsWhenCollapsed),
    showAIRailAutomatically: booleanValue(sourceDisplay.showAIRailAutomatically, baseDisplay.showAIRailAutomatically),
    rememberLastSidebarState: booleanValue(sourceDisplay.rememberLastSidebarState, baseDisplay.rememberLastSidebarState)
  };

  const baseSchedule = base.schedule || DEFAULT_APPEARANCE.schedule;
  result.schedule = {
    enabled: booleanValue(sourceSchedule.enabled ?? source.automaticScheduleEnabled, baseSchedule.enabled),
    workStart: validClock(sourceSchedule.workStart, baseSchedule.workStart),
    workEnd: validClock(sourceSchedule.workEnd, baseSchedule.workEnd),
    workPersonality: enumValue(sourceSchedule.workPersonality, PERSONALITIES, baseSchedule.workPersonality),
    afterHoursPersonality: enumValue(sourceSchedule.afterHoursPersonality, PERSONALITIES, baseSchedule.afterHoursPersonality)
  };

  const baseDevice = base.deviceOverride || DEFAULT_APPEARANCE.deviceOverride;
  result.deviceOverride = {
    enabled: booleanValue(sourceDevice.enabled, baseDevice.enabled),
    colorMode: sourceDevice.colorMode == null ? null : enumValue(sourceDevice.colorMode, COLOR_MODES, baseDevice.colorMode || result.colorMode),
    density: sourceDevice.density == null ? null : enumValue(sourceDevice.density, DENSITIES, baseDevice.density || result.density)
  };
  result.onboardingComplete = booleanValue(source.onboardingComplete, base.onboardingComplete || false);
  result.version = APPEARANCE_SCHEMA_VERSION;
  return result;
}

export function migrateLegacyAppearance(savedSettings = {}, existing = {}) {
  const values = savedSettings?.values && typeof savedSettings.values === "object" ? savedSettings.values : savedSettings;
  return normalizeAppearance({
    ...existing,
    colorMode: existing.colorMode ?? values?.theme,
    density: existing.density ?? values?.["interface-density"],
    motion: existing.motion ?? values?.motion,
    readerWidth: existing.readerWidth ?? values?.["reader-width"]
  });
}

export function scopedAppearanceStorageKey(userId) {
  const safe = String(userId || "device").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 80) || "device";
  return `${APPEARANCE_STORAGE_KEY}:${safe}`;
}

export function loadAppearance(storage = globalThis.localStorage, { userId = "", legacySettings = {} } = {}) {
  try {
    const scoped = userId ? storage?.getItem(scopedAppearanceStorageKey(userId)) : null;
    const device = storage?.getItem(APPEARANCE_STORAGE_KEY);
    const parsed = JSON.parse(scoped || device || "null");
    return migrateLegacyAppearance(legacySettings, parsed || {});
  } catch {
    return migrateLegacyAppearance(legacySettings);
  }
}

export function saveAppearance(preferences, storage = globalThis.localStorage, { userId = "" } = {}) {
  const normalized = normalizeAppearance(preferences);
  try {
    const serialized = JSON.stringify(normalized);
    storage?.setItem(APPEARANCE_STORAGE_KEY, serialized);
    if (userId) storage?.setItem(scopedAppearanceStorageKey(userId), serialized);
  } catch {
    // Appearance remains active in memory when private browsing or quota blocks storage.
  }
  return normalized;
}

function minutesSinceMidnight(date) {
  return date.getHours() * 60 + date.getMinutes();
}

function clockMinutes(value) {
  const [hours, minutes] = String(value).split(":").map(Number);
  return hours * 60 + minutes;
}

export function scheduledPersonality(schedule, date = new Date()) {
  const normalized = normalizeAppearance({ schedule }).schedule;
  if (!normalized.enabled) return null;
  const current = minutesSinceMidnight(date);
  const start = clockMinutes(normalized.workStart);
  const end = clockMinutes(normalized.workEnd);
  const inWorkWindow = start <= end ? current >= start && current < end : current >= start || current < end;
  return inWorkWindow ? normalized.workPersonality : normalized.afterHoursPersonality;
}

export function appearanceFeatureAccess(planValue, field, value) {
  const plan = String(planValue || "preview").toLowerCase();
  const rank = PLAN_RANK[plan] ?? 0;
  let minimumPlan = "preview";
  if (field === "personality" && value !== "professional") minimumPlan = "pro";
  if (field === "density" && value !== "standard") minimumPlan = "pro";
  if (field === "accent" && !["blue", "copper"].includes(value)) minimumPlan = "pro";
  if (["schedule", "deviceOverride", "display"].includes(field)) minimumPlan = "pro";
  if (field === "workspacePolicy") minimumPlan = "teams";
  if (field === "organizationPolicy") minimumPlan = "business";
  if (field === "brandTreatment") minimumPlan = "enterprise";
  return { allowed: rank >= PLAN_RANK[minimumPlan], minimumPlan };
}

export function resolveAppearance(preferences, {
  systemDark = globalThis.matchMedia?.("(prefers-color-scheme: dark)")?.matches === true,
  reduceMotion = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true,
  increasedContrast = globalThis.matchMedia?.("(prefers-contrast: more)")?.matches === true,
  workspacePolicy = null,
  now = new Date(),
  allowScheduledChange = true
} = {}) {
  const resolved = normalizeAppearance(preferences);
  if (resolved.deviceOverride.enabled) {
    if (resolved.deviceOverride.colorMode) resolved.colorMode = resolved.deviceOverride.colorMode;
    if (resolved.deviceOverride.density) resolved.density = resolved.deviceOverride.density;
  }
  const automaticPersonality = allowScheduledChange ? scheduledPersonality(resolved.schedule, now) : null;
  if (automaticPersonality) resolved.personality = automaticPersonality;
  if (workspacePolicy && typeof workspacePolicy === "object") {
    const allowOverrides = workspacePolicy.allowUserOverrides !== false && workspacePolicy.usersMayOverride !== false;
    if (!allowOverrides) {
      resolved.personality = enumValue(workspacePolicy.defaultPersonality, PERSONALITIES, resolved.personality);
      resolved.density = enumValue(workspacePolicy.defaultDensity, DENSITIES, resolved.density);
    }
    const approved = Array.isArray(workspacePolicy.approvedAccents)
      ? workspacePolicy.approvedAccents.map((value) => enumValue(value, ACCENTS, "")).filter(Boolean)
      : [];
    if (approved.length && !approved.includes(resolved.accent)) resolved.accent = approved[0];
  }
  const effectiveMode = resolved.colorMode === "system" ? (systemDark ? "dark" : "light") : resolved.colorMode;
  const effectiveMotion = reduceMotion ? "reduced" : resolved.motion;
  return {
    ...resolved,
    effectiveMode,
    effectiveMotion,
    accessibility: {
      reduceMotion,
      increasedContrast: increasedContrast || resolved.display.highContrast,
      reduceTranslucency: resolved.display.reduceTranslucency
    }
  };
}

export function applyAppearance(preferences, options = {}) {
  const root = options.root || globalThis.document?.documentElement;
  const resolved = resolveAppearance(preferences, options);
  if (!root) return resolved;
  root.dataset.kahvorAppearanceVersion = String(APPEARANCE_SCHEMA_VERSION);
  root.dataset.kahvorPersonality = resolved.personality;
  root.dataset.kahvorColorMode = resolved.colorMode;
  root.dataset.kahvorTheme = resolved.effectiveMode;
  root.dataset.kahvorDensity = resolved.density;
  root.dataset.kahvorAccent = resolved.accent;
  root.dataset.kahvorMotion = resolved.effectiveMotion;
  root.dataset.kahvorReader = resolved.readerWidth;
  root.dataset.kahvorContrast = resolved.accessibility.increasedContrast ? "high" : "standard";
  root.dataset.kahvorTranslucency = resolved.accessibility.reduceTranslucency ? "reduced" : "full";
  root.dataset.kahvorFont = resolved.display.useSystemFont ? "system" : "kahvor";
  root.dataset.kahvorPreviewText = resolved.display.showMessagePreviewText ? "shown" : "hidden";
  root.dataset.kahvorAvatars = resolved.display.showAvatars ? "shown" : "hidden";
  root.dataset.kahvorUnread = resolved.display.emphasizeUnreadMessages ? "emphasized" : "standard";
  root.dataset.kahvorTimestamps = resolved.display.showCompactTimestamps ? "compact" : "standard";
  root.dataset.kahvorSidebarLabels = resolved.display.displaySidebarLabelsWhenCollapsed ? "shown" : "hidden";
  root.dataset.kahvorAiRail = resolved.display.showAIRailAutomatically ? "automatic" : "manual";
  root.style.colorScheme = resolved.effectiveMode;
  root.style.setProperty("--appearance-preview-lines", String(resolved.display.previewLineCount));
  root.style.setProperty("--appearance-message-body-scale", String(resolved.display.messageBodyScale));
  const themeColor = globalThis.document?.querySelector?.('meta[name="theme-color"]');
  if (themeColor) themeColor.content = resolved.effectiveMode === "dark" ? "#151412" : "#eee9e1";
  return resolved;
}

export function appearanceCloudPayload(preferences) {
  const value = normalizeAppearance(preferences);
  return {
    appearanceVersion: value.version,
    appearancePersonality: value.personality,
    colorMode: value.colorMode,
    theme: value.colorMode,
    density: value.density,
    accent: value.accent,
    motion: value.motion,
    readerWidth: value.readerWidth,
    showMessagePreviewText: value.display.showMessagePreviewText,
    previewLineCount: value.display.previewLineCount,
    showAvatars: value.display.showAvatars,
    emphasizeUnreadMessages: value.display.emphasizeUnreadMessages,
    showCompactTimestamps: value.display.showCompactTimestamps,
    messageBodyScale: value.display.messageBodyScale,
    reduceTranslucency: value.display.reduceTranslucency,
    highContrast: value.display.highContrast,
    useSystemFont: value.display.useSystemFont,
    displaySidebarLabelsWhenCollapsed: value.display.displaySidebarLabelsWhenCollapsed,
    showAIRailAutomatically: value.display.showAIRailAutomatically,
    rememberLastSidebarState: value.display.rememberLastSidebarState,
    automaticScheduleEnabled: value.schedule.enabled,
    automaticScheduleConfiguration: value.schedule,
    deviceOverride: value.deviceOverride
  };
}

export function mergeRemoteAppearance(localPreferences, remotePreferences) {
  if (!remotePreferences || typeof remotePreferences !== "object") return normalizeAppearance(localPreferences);
  const remoteHasAppearance = [
    "appearanceVersion", "appearancePersonality", "personality", "colorMode", "theme", "density", "accent", "motion"
  ].some((key) => Object.prototype.hasOwnProperty.call(remotePreferences, key));
  return remoteHasAppearance ? normalizeAppearance(remotePreferences, localPreferences) : normalizeAppearance(localPreferences);
}

export function safeAppearanceTelemetry(eventType, preferences, extra = {}) {
  const value = normalizeAppearance(preferences);
  return {
    eventType: String(eventType || "appearance_change").replace(/[^a-z0-9_.-]/gi, "_").slice(0, 80),
    personality: value.personality,
    colorMode: value.colorMode,
    density: value.density,
    accent: value.accent,
    motion: value.motion,
    outcome: ["started", "succeeded", "failed", "cancelled"].includes(extra.outcome) ? extra.outcome : "succeeded"
  };
}
